import React, {
  memo, useEffect, useRef, useState,
} from 'react';
import IconFont from '$/components/IconFont';
import classNames from 'classnames';
import { getWebpUrl } from '$/utils/image';
import styles from './index.module.scss';
import { MarkResult } from '../../types';

function MarkResultPopover({
  list,
  width,
  height,
  selectedIndex,
  custom,
  onSelect,
  url,
}: {
  list: MarkResult[];
  selectedIndex?: number;
  custom?: string;
  url: string;
  width: number;
  height: number;
  onSelect: (params: { name?: string; index: number; }) => void; }) {
  const [customName, setCustomName] = useState<string>(custom || '');
  const [imagePieces, setImagePieces] = useState<Record<string, string>>({});
  const selectingRef = useRef(false);
  useEffect(() => {
    if (selectedIndex === -1) {
      setCustomName(custom || '');
    } else if (customName) {
      setCustomName('');
    }
  }, [selectedIndex, custom]);
  const handleCustomNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCustomName(e.target.value);
  };
  const handleSelect = ({ index, name }: { index: number; name?: string; }) => {
    if (selectedIndex === index) return;
    onSelect({ index, name });
    queueMicrotask(() => {
      selectingRef.current = false;
    });
  };
  const handleCustomNameBlur = () => {
    if (selectingRef.current) return;
    if (selectedIndex !== -1) return;
    onSelect({ index: -1, name: customName });
  };

  /**
     * 创建识别区域缩略图
     */
  useEffect(() => {
    if (list.length === 0) {
      setImagePieces({});
    }

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = getWebpUrl({ url }); // ATTENTION: 使用云处理图片避免体积过大

    img.onload = () => {
      const pieces: Record<string, string> = {};

      list.forEach((item, index) => {
        if (item.box) {
          const {
            x, y, w, h,
          } = item.box;

          // 归一化坐标直接映射到缩略图的实际尺寸
          const cropX = x * width;
          const cropY = y * height;
          const cropW = w * width;
          const cropH = h * height;

          // 创建 canvas 裁切图片
          const canvas = document.createElement('canvas');
          canvas.width = cropW;
          canvas.height = cropH;

          const ctx = canvas.getContext('2d');
          if (ctx) {
            ctx.drawImage(img, cropX, cropY, cropW, cropH, 0, 0, cropW, cropH);

            pieces[index] = canvas.toDataURL('image/png');
          }
        }

        setImagePieces(pieces);
      });
    };
  }, [width, height, list, url]);

  return (
    <div className={styles.MarkResultPopover}>
      <div className={styles.MarkResultPopoverContent}>
        <div className={styles.MarkResultPopoverContentTitle}>
          标注识别
        </div>
        <div className={styles.list}>
          {
            list.map((item, i) => {
              return (
                <div
                  className={classNames(styles.item, { [styles.active]: i === selectedIndex })}
                  key={i}
                  onMouseDown={(event) => {
                    const target = event.target as HTMLElement | null;
                    if (target?.tagName === 'INPUT') return;
                    selectingRef.current = true;
                  }}
                  onClick={() => handleSelect({ index: i })}
                >
                  <div className={styles.itemImg}>
                    <img className={styles.img} src={imagePieces[i]} alt={item.name} crossOrigin="anonymous" />
                  </div>
                  <div className={styles.itemName}>{item.name}</div>
                  <div className={styles.selectIcon}>
                    <IconFont name="checkBoxMark" fontSize={10} />
                  </div>
                </div>
              );
            })

          }
          <div
            className={classNames(styles.item, { [styles.active]: selectedIndex === -1 })}
            onMouseDown={(event) => {
              const target = event.target as HTMLElement | null;
              if (target?.tagName === 'INPUT') return;
              selectingRef.current = true;
            }}
            onClick={() => {
              if (!customName) return;
              handleSelect({ index: -1, name: customName });
            }}
          >
            <div className={styles.itemImg}>
              <svg className={styles.customIcon} width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
                <path d="M8.99609 0C13.9667 5.15422e-07 17.9961 4.02944 17.9961 9C17.9961 13.9706 13.9667 18 8.99609 18H2.5459C2.24333 18 1.97126 18.0008 1.74609 17.9824C1.51212 17.9633 1.2632 17.9201 1.01953 17.7959C0.666853 17.6162 0.379934 17.3292 0.200195 16.9766C0.0760392 16.7329 0.0327888 16.484 0.0136719 16.25C-0.00472535 16.0248 -0.0039065 15.7528 -0.00390625 15.4502V9C-0.00390625 4.02944 4.02554 6.08083e-06 8.99609 0Z" fillOpacity="0.16" />
              </svg>
            </div>
            <div className={styles.itemName}>
              <input
                type="text"
                value={customName}
                onChange={handleCustomNameChange}
                onBlur={handleCustomNameBlur}
                className={styles.input}
                minLength={0}
                maxLength={10}
                placeholder="自定义名称"
              />
            </div>
            <div className={styles.selectIcon}>
              <IconFont name="checkBoxMark" fontSize={10} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default memo(MarkResultPopover);
